import { useEffect, useState } from 'react'
import type { FormTemplateScope, FormTemplateSummary } from '@/types'
import { FormTemplateScope as ScopeEnum } from '@/types'
import { MagnifyingGlassIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline'

export type TemplateScopeFilter = FormTemplateScope | 'ALL'

interface TemplateSidebarProps {
  templates: FormTemplateSummary[]
  loading: boolean
  activeTemplateId?: string
  scope: TemplateScopeFilter
  onSelectTemplate: (templateId: string) => void
  onRefresh: () => void
  onScopeChange: (scope: TemplateScopeFilter) => void
  onSearch: (value: string) => void
  onCreateTemplate: () => void
  onDeleteTemplate: (template: FormTemplateSummary) => void
}

const scopeOptions: Array<{ label: string; value: TemplateScopeFilter }> = [
  { label: '全部範圍', value: 'ALL' },
  { label: '組織模板', value: ScopeEnum.ORGANIZATION },
  { label: '專案模板', value: ScopeEnum.PROJECT },
  { label: '個人模板', value: ScopeEnum.PERSONAL },
  { label: '公共市場', value: ScopeEnum.MARKET },
]

export const TemplateSidebar = ({
  templates,
  loading,
  activeTemplateId,
  scope,
  onSelectTemplate,
  onRefresh,
  onScopeChange,
  onSearch,
  onCreateTemplate,
  onDeleteTemplate,
}: TemplateSidebarProps) => {
  const [keyword, setKeyword] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(keyword.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [keyword, onSearch])

  return (
    <aside className="w-full lg:w-64 bg-gray-50 border-r border-gray-200 h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">表單模板</h2>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center rounded-md border border-transparent bg-white p-1 text-gray-500 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="重新整理"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <button
          type="button"
          onClick={onCreateTemplate}
          className="w-full rounded-md border border-primary-500 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 mt-3"
        >
          新增模板
        </button>

        <div>
          <label htmlFor="template-scope" className="block text-xs font-medium text-gray-500 mb-1">
            範圍
          </label>
          <select
            id="template-scope"
            value={scope}
            onChange={(e) => onScopeChange(e.target.value as TemplateScopeFilter)}
            className="w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {scopeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜尋模板名稱或標籤"
            className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && templates.length === 0 ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-10 rounded-md bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            尚無符合條件的模板
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {templates.map((template) => {
              const isActive = template.id === activeTemplateId
              return (
                <li key={template.id}>
                  <div
                    className={`flex items-start justify-between gap-2 px-4 py-3 transition-colors duration-150 ${
                      isActive
                        ? 'bg-primary-100 text-primary-900'
                        : 'hover:bg-white focus-within:bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectTemplate(template.id)}
                      className="flex-1 text-left"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{template.name}</span>
                        {template.latestVersion && (
                          <span className={`text-xs ${isActive ? 'text-primary-700' : 'text-gray-400'}`}>
                            v{template.latestVersion.versionNumber}
                          </span>
                        )}
                      </div>
                      <div className={`mt-1 text-xs ${isActive ? 'text-primary-700' : 'text-gray-500'} space-x-2`}>
                        <span>{template.scopeType}</span>
                        {template.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] ${
                              isActive ? 'bg-primary-200 text-primary-800' : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </button>
                    <button
                      type="button"
                      title="刪除模板"
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onDeleteTemplate(template)
                      }}
                      disabled={loading}
                      className={`ml-2 inline-flex items-center rounded-md border border-transparent p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                        loading ? 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-gray-400' : ''
                      }`}
                      aria-label={`刪除模板 ${template.name}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </aside>
  )
}

export default TemplateSidebar
